import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { servicesApi } from '../../../services/api'
import { getErrorMessage } from '../../../utils/errors'
import TableActionButton from '../../../components/TableActionButton'
import { useSortableData } from '../../../hooks/useSortableData'
import SortableTh from '../../../components/SortableTh'

interface Service {
  id: number
  name: string
  current_cost: number
  unit: string
  is_active: boolean
  updated_at: string
}

interface ServiceForm {
  name: string
  current_cost: string
  unit: string
}

const emptyForm: ServiceForm = { name: '', current_cost: '', unit: '' }

export default function ServicesTab() {
  const [services, setServices] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<ServiceForm>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [togglingId, setTogglingId] = useState<number | null>(null)

  const fetchServices = async () => {
    try {
      setLoadError('')
      const res = await servicesApi.list()
      setServices(res.data.services || [])
    } catch (err) {
      console.error('Error fetching services:', err)
      setLoadError('No se pudieron cargar los servicios. Intenta recargar la página.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchServices()
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const openNew = () => {
    setForm(emptyForm)
    setEditingId(null)
    setError('')
    setShowForm(true)
  }

  const openEdit = (s: Service) => {
    setForm({ name: s.name, current_cost: String(s.current_cost), unit: s.unit || '' })
    setEditingId(s.id)
    setError('')
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      setError('El nombre es requerido')
      return
    }
    if (!form.unit.trim()) {
      setError('La unidad es requerida')
      return
    }
    if (form.current_cost === '' || parseFloat(form.current_cost) < 0) {
      setError('Ingresa un costo válido')
      return
    }
    setSaving(true)
    setError('')
    try {
      const payload = {
        name: form.name.trim(),
        current_cost: parseFloat(form.current_cost),
        unit: form.unit.trim(),
      }
      if (editingId) {
        await servicesApi.update(editingId, payload)
        toast.success('Servicio actualizado')
      } else {
        await servicesApi.create(payload)
        toast.success('Servicio creado')
      }
      setShowForm(false)
      fetchServices()
    } catch (err: any) {
      setError(getErrorMessage(err, 'Error al guardar el servicio'))
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (s: Service) => {
    setTogglingId(s.id)
    try {
      await servicesApi.update(s.id, { is_active: !s.is_active })
      toast.success(s.is_active ? 'Servicio desactivado' : 'Servicio activado')
      fetchServices()
    } catch (err: any) {
      toast.error(getErrorMessage(err, 'Error al actualizar el servicio'))
    } finally {
      setTogglingId(null)
    }
  }

  const { sortedData: sortedServices, sortConfig, requestSort } = useSortableData(services)

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  const zeroCostCount = services.filter((s) => s.is_active && Number(s.current_cost) === 0).length

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-espresso-500 max-w-2xl">
          Servicios contratados a terceros (flete, maquila, mantenimiento, etc). Evita crear duplicados — en su lugar,
          edita el costo del servicio existente.
        </p>
        <button
          onClick={openNew}
          className="shrink-0 ml-4 px-4 py-2 bg-terracotta-600 text-white rounded-md hover:bg-terracotta-700 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500"
        >
          + Nuevo Servicio
        </button>
      </div>

      {zeroCostCount > 0 && (
        <div className="bg-amber-50 text-amber-800 px-4 py-3 rounded-md text-sm mb-4" role="alert">
          {zeroCostCount} servicio{zeroCostCount > 1 ? 's' : ''} activo{zeroCostCount > 1 ? 's' : ''} con costo $0.00.
          Establece su costo real antes de usarlo.
        </div>
      )}

      {loadError && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-md text-sm mb-4" role="alert">
          {loadError}
        </div>
      )}

      {!loadError && services.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">No hay servicios registrados aún.</p>
        </div>
      ) : (
        <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-espresso-100">
              <thead className="bg-cream-50">
                <tr>
                  <SortableTh label="Nombre" sortKey="name" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Costo" sortKey="current_cost" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Unidad" sortKey="unit" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Actualizado" sortKey="updated_at" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Estado" sortKey="is_active" sortConfig={sortConfig} onSort={requestSort} />
                  <th className="px-6 py-3 text-right text-xs font-medium text-espresso-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-cream-50 divide-y divide-espresso-100">
                {sortedServices.map((s) => (
                  <tr key={s.id} className="hover:bg-cream-100">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-espresso-800">{s.name}</td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${
                        Number(s.current_cost) === 0 ? 'text-amber-700' : 'text-green-700'
                      }`}
                    >
                      ${Number(s.current_cost).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-espresso-500">{s.unit || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-espresso-500">
                      {s.updated_at ? new Date(s.updated_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          s.is_active ? 'bg-green-100 text-green-800' : 'bg-cream-200 text-espresso-700'
                        }`}
                      >
                        {s.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end gap-1">
                        <TableActionButton onClick={() => openEdit(s)} variant="primary">
                          Editar
                        </TableActionButton>
                        <TableActionButton
                          onClick={() => handleToggleActive(s)}
                          disabled={togglingId === s.id}
                          variant="danger"
                        >
                          {togglingId === s.id ? 'Guardando...' : s.is_active ? 'Desactivar' : 'Activar'}
                        </TableActionButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">
                {editingId ? 'Editar Servicio' : 'Nuevo Servicio'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              {error && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm" role="alert">
                  {error}
                </div>
              )}
              <div>
                <label htmlFor="service-name" className="block text-sm font-medium text-espresso-700 mb-1">
                  Nombre *
                </label>
                <input
                  id="service-name"
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  required
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="service-cost" className="block text-sm font-medium text-espresso-700 mb-1">
                    Costo *
                  </label>
                  <input
                    id="service-cost"
                    type="number"
                    name="current_cost"
                    value={form.current_cost}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="service-unit" className="block text-sm font-medium text-espresso-700 mb-1">
                    Unidad *
                  </label>
                  <input
                    id="service-unit"
                    type="text"
                    name="unit"
                    value={form.unit}
                    onChange={handleChange}
                    placeholder="viaje, hora, servicio..."
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50"
                >
                  {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
